import { useEffect, useMemo, useCallback, useRef, useState, useContext } from "react"
import styles from "./index.module.scss"
import PicViewerBottom from "../PicViewerBottom"
import PicViewerBottomMultiple from "../PicViewerBottomMultiple"
import ImageRenderBox from "../components/ImageRenderBox"
import { Button, ConfigProvider, Popover, Segmented } from "antd"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { useDrop } from "react-dnd"
import Iconfont from "@/utils/Iconfont"
import { PictureViewerContext } from "../hooks/PictureViewerContext"
import { MULTIPLE_IMAGE_DRAG_TYPE } from "../PicViewerImageList"

/**
 * PicViewerCenter - 中间画布区域
 * 支持三种模式：
 *   single   单图
 *   parallel 并列
 *   compare  对比
 *   multiple 多图
 */
export default function PicViewerCenter({
    image = {},
    onDownload,
    extraButtons = [],
    onPrev,
    onNext,
    onAnnotationChange,
    onStatusChange,
    onConfirm,
    multipleItems = [],
    onMultipleFocus,
    onMultipleAnnotationChange,
    onMultipleStatusChange,
    onMultipleConfirm,
    onMultiplePinToggle,
    onMultipleDrop,
    onMultipleBatchShift,
}) {

    const context = useContext(PictureViewerContext)
    const {
        currentImage,
        hasNext,
        hasPrev,
        viewMode,
        setViewMode,
        parallelSyncLock,
        setParallelSyncLock,
        multipleSyncLocks,
        setMultipleSyncLocks,
    } = context
    const {
        floatingLabelToolVisibleStatuses,
    } = context.controls

    const [comparePosition, setComparePosition] = useState(50)

    useEffect(() => {
        setComparePosition(50)
    }, [image.id, image.url])

    const isVideo = useMemo(() => {
        return isVideoUrl(image?.url)
    }, [image?.url])
    const extraImages = image.extraImages || []
    const activeExtraIndex = image._activeExtraIndex || 0
    const activeExtraImage = extraImages[activeExtraIndex]
    const activeMaskUrl = useBlueMaskOverlay(image._showMask ? activeExtraImage?.maskUrl : null)
    const availableModes = useMemo(() => {
        if (!extraImages.length) {
            return [
                { label: '单图', value: 'single', icon: <Iconfont type="icon-view-single" /> },
                { label: '多图', value: 'multiple', icon: <Iconfont type="icon-view-batch" /> },
            ]
        }
        return [
            { label: '并列', value: 'parallel', icon: <Iconfont type="icon-view-para" /> },
            { label: '对比', value: 'compare', icon: <Iconfont type="icon-view-compare" />, disabled: isVideo },
            { label: '单图', value: 'single', icon: <Iconfont type="icon-view-single" /> },
            { label: '多图', value: 'multiple', icon: <Iconfont type="icon-view-batch" /> },
        ]
    }, [extraImages.length, isVideo])
    useEffect(() => {
        const firstAvailableMode = availableModes.find(mode => !mode.disabled)?.value || 'single'
        const matchedMode = availableModes.find(mode => mode.value === viewMode)
        if (!matchedMode || matchedMode.disabled) setViewMode?.(firstAvailableMode)
    }, [viewMode, availableModes, setViewMode])
    const shouldShowModeChanger = useMemo(() => {
        return extraImages.length > 0 && availableModes.length > 1
    }, [extraImages.length, availableModes.length])
    const mainCanvasItem = useMemo(() => ({
        ...image,
        name: image?.name || '交付图',
        annotations: image?.annotations || [],
        readonly: image?.readOnly,
        downloadable: false,
        pinned: false,
        extraRender: null,
    }), [image])
    const referenceCanvasItem = useMemo(() => {
        if (!activeExtraImage) return null
        return {
            ...activeExtraImage,
            name: activeExtraImage.label,
            annotations: activeExtraImage.annotations || [],
            readonly: true,
            downloadable: false,
            pinned: false,
            overlayUrl: activeMaskUrl,
            extraRender: null,
        }
    }, [activeExtraImage, activeMaskUrl])
    const noop = useCallback(() => {}, [])
    
    // 对比图是否显示浮动标注工具
    const referenceCanvasShowFloatingLabelTool = useMemo(() => {
        return referenceCanvasItem?.annotations?.length > 0
    }, [referenceCanvasItem])

    // 结果图是否显示浮动标注工具
    const showFloatingLabelTool = useMemo(() => {
        return floatingLabelToolVisibleStatuses.includes(currentImage?.status)
    }, [floatingLabelToolVisibleStatuses, currentImage?.status])

    const panelRefs = useRef({})
    const panelTransformSnapshotsRef = useRef({})
    const prevMainImageKeyRef = useRef(null)
    const prevReferenceLabelRef = useRef(null)
    const parallelSyncResetOnActivateRef = useRef(false)
    // 同步组以最近一次被用户操作的视频为主控源，避免多个 timeupdate 双向传播造成进度抖动。
    const videoSyncLeaderRef = useRef(null)

    const setPanelRef = useCallback((panelId, instance) => {
        if (!panelId) return
        if (instance) {
            panelRefs.current[panelId] = instance
            return
        }
        delete panelRefs.current[panelId]
    }, [])

    const getActiveSyncEdges = useCallback(() => {
        if (viewMode === 'parallel' && parallelSyncLock) {
            return [['parallel-reference', 'parallel-main']]
        }
        if (viewMode === 'multiple') {
            const edges = []
            if (multipleSyncLocks?.[0]) {
                edges.push(['multiple-0', 'multiple-1'])
            }
            if (multipleSyncLocks?.[1]) {
                edges.push(['multiple-1', 'multiple-2'])
            }
            return edges
        }
        return []
    }, [multipleSyncLocks, parallelSyncLock, viewMode])

    const getConnectedPanelIds = useCallback((startPanelId, edges = getActiveSyncEdges()) => {
        if (!startPanelId) return []
        const adjacencyMap = new Map()
        edges.forEach(([leftPanelId, rightPanelId]) => {
            adjacencyMap.set(leftPanelId, [...(adjacencyMap.get(leftPanelId) || []), rightPanelId])
            adjacencyMap.set(rightPanelId, [...(adjacencyMap.get(rightPanelId) || []), leftPanelId])
        })
        if (!adjacencyMap.has(startPanelId)) return []

        const queue = [startPanelId]
        const visited = new Set([startPanelId])
        while (queue.length) {
            const panelId = queue.shift()
            ;(adjacencyMap.get(panelId) || []).forEach((nextPanelId) => {
                if (visited.has(nextPanelId)) return
                visited.add(nextPanelId)
                queue.push(nextPanelId)
            })
        }
        return [...visited]
    }, [getActiveSyncEdges])

    const resetPanels = useCallback((panelIds = []) => {
        panelIds.forEach((panelId) => {
            panelRefs.current[panelId]?.resetTransform?.({ source: 'sync' })
        })
    }, [])

    const handleSyncTransform = useCallback((panelId, nextTransform, meta = {}) => {
        if (meta?.source === 'sync' || meta?.source === 'preserve') return
        const connectedPanelIds = getConnectedPanelIds(panelId)
        if (connectedPanelIds.length <= 1) return

        connectedPanelIds
            .filter(targetPanelId => targetPanelId !== panelId)
            .forEach((targetPanelId) => {
                panelRefs.current[targetPanelId]?.setTransform?.(nextTransform, { source: 'sync' })
            })
    }, [getConnectedPanelIds])

    const handlePanelTransform = useCallback((panelId, nextTransform, meta = {}) => {
        panelTransformSnapshotsRef.current[panelId] = nextTransform
        handleSyncTransform(panelId, nextTransform, meta)
    }, [handleSyncTransform])

    const syncConnectedVideos = useCallback((panelId, event) => {
        const connectedPanelIds = getConnectedPanelIds(panelId)
        if (connectedPanelIds.length <= 1) return
        connectedPanelIds
            .filter(targetPanelId => targetPanelId !== panelId)
            .forEach((targetPanelId) => {
                panelRefs.current[targetPanelId]?.syncVideo?.({ ...event, source: 'sync' })
            })
    }, [getConnectedPanelIds])

    // 只转发用户产生的媒体事件；由同步命令触发的事件到此终止，防止循环广播。
    const handleVideoEvent = useCallback((panelId, event) => {
        if (event?.source === 'sync') return
        const connectedPanelIds = getConnectedPanelIds(panelId)
        if (connectedPanelIds.length <= 1) return

        // 同步组只允许最近一次用户操作的视频持续广播进度，防止各视频的 timeupdate 相互争抢主进度。
        if (event?.type === 'timeupdate') {
            const leaderPanelId = videoSyncLeaderRef.current
            if (leaderPanelId && leaderPanelId !== panelId && connectedPanelIds.includes(leaderPanelId)) return
            if (!leaderPanelId || !connectedPanelIds.includes(leaderPanelId)) {
                videoSyncLeaderRef.current = panelId
            }
        } else {
            videoSyncLeaderRef.current = panelId
        }
        syncConnectedVideos(panelId, event)
    }, [getConnectedPanelIds, syncConnectedVideos])

    // 播放控制必须在原始点击/按键调用栈内同步执行，否则浏览器可能拦截其他带声音视频的 play()。
    const handleVideoToggle = useCallback((panelId) => {
        const videoState = panelRefs.current[panelId]?.getVideoState?.()
        if (!videoState) return
        const connectedPanelIds = getConnectedPanelIds(panelId)
        const targetPanelIds = connectedPanelIds.length > 1 ? connectedPanelIds : [panelId]
        const command = {
            type: videoState.isPlaying ? 'pause' : 'play',
            currentTime: videoState.currentTime,
        }

        // 在点击或按键事件调用栈内直接控制整组视频，保留浏览器的用户激活权限。
        videoSyncLeaderRef.current = panelId
        targetPanelIds.forEach((targetPanelId) => {
            panelRefs.current[targetPanelId]?.syncVideo?.(command)
        })
    }, [getConnectedPanelIds])

    // 用指定面板作为主控源，将当前播放状态和时间一次性对齐到目标面板。
    const syncVideoStateToPanels = useCallback((panelId, targetPanelIds) => {
        const videoState = panelRefs.current[panelId]?.getVideoState?.()
        if (!videoState) return
        videoSyncLeaderRef.current = panelId
        const command = {
            type: videoState.isPlaying ? 'play' : 'pause',
            currentTime: videoState.currentTime,
        }
        targetPanelIds
            .filter(targetPanelId => targetPanelId !== panelId)
            .forEach(targetPanelId => panelRefs.current[targetPanelId]?.syncVideo?.(command))
    }, [])

    const syncVideoStateFromPanel = useCallback((panelId) => {
        syncVideoStateToPanels(panelId, getConnectedPanelIds(panelId))
    }, [getConnectedPanelIds, syncVideoStateToPanels])

    const activateParallelSyncLock = useCallback(() => {
        const nextValue = !parallelSyncLock
        parallelSyncResetOnActivateRef.current = nextValue
        setParallelSyncLock(nextValue)
        if (nextValue) {
            // 开锁动作本身也是用户手势，直接同步可避免正在播放的视频被浏览器拦截。
            syncVideoStateToPanels('parallel-main', ['parallel-reference', 'parallel-main'])
        }
    }, [parallelSyncLock, setParallelSyncLock, syncVideoStateToPanels])

    const activateMultipleSyncLock = useCallback((lockIndex) => {
        if (![0, 1].includes(lockIndex)) return
        const nextValue = [...(multipleSyncLocks || [false, false])]
        nextValue[lockIndex] = !nextValue[lockIndex]
        setMultipleSyncLocks(nextValue)
        if (nextValue[lockIndex]) {
            const edges = []
            if (nextValue[0]) edges.push(['multiple-0', 'multiple-1'])
            if (nextValue[1]) edges.push(['multiple-1', 'multiple-2'])
            const startPanelId = lockIndex === 0 ? 'multiple-0' : 'multiple-1'
            const connectedPanelIds = getConnectedPanelIds(startPanelId, edges)
            resetPanels(connectedPanelIds)
            syncVideoStateToPanels(startPanelId, connectedPanelIds)
        }
    }, [getConnectedPanelIds, multipleSyncLocks, resetPanels, setMultipleSyncLocks, syncVideoStateToPanels])

    const multipleSyncSignature = useMemo(() => (
        multipleItems
            .map((item) => item?.image?.id || item?.image?.imageId || item?.image?.url || item?.imageIndex || '')
            .join('|')
    ), [multipleItems])

    const mainImageKey = useMemo(() => (
        image?.id || image?.imageId || image?.url || null
    ), [image?.id, image?.imageId, image?.url])
    const currentReferenceLabel = referenceCanvasItem?.label || null
    const shouldPreserveReferenceTransform = useMemo(() => {
        const prevMainImageKey = prevMainImageKeyRef.current
        const prevReferenceLabel = prevReferenceLabelRef.current
        return viewMode === 'parallel'
            && !!prevMainImageKey
            && prevMainImageKey !== mainImageKey
            && !!prevReferenceLabel
            && prevReferenceLabel === currentReferenceLabel
    }, [currentReferenceLabel, mainImageKey, viewMode])
    const preservedReferenceTransform = shouldPreserveReferenceTransform
        ? panelTransformSnapshotsRef.current['parallel-reference']
        : null
    const preservedMainTransform = shouldPreserveReferenceTransform && parallelSyncLock
        ? panelTransformSnapshotsRef.current['parallel-main']
        : null

    useEffect(() => {
        if (!shouldPreserveReferenceTransform) return undefined
        if (parallelSyncResetOnActivateRef.current) return undefined

        const frameId = window.requestAnimationFrame(() => {
            if (preservedReferenceTransform) {
                panelRefs.current['parallel-reference']?.setTransform?.(preservedReferenceTransform, { source: 'preserve' })
            }
            if (preservedMainTransform) {
                panelRefs.current['parallel-main']?.setTransform?.(preservedMainTransform, { source: 'preserve' })
            }
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [preservedMainTransform, preservedReferenceTransform, shouldPreserveReferenceTransform])

    useEffect(() => {
        if (viewMode !== 'parallel' || !parallelSyncLock) return undefined
        const shouldForceReset = parallelSyncResetOnActivateRef.current
        const frameId = window.requestAnimationFrame(() => {
            // 同一帧内先稳定画布状态，再对齐视频，避免两组异步任务产生执行顺序差异。
            if (!shouldPreserveReferenceTransform || shouldForceReset) {
                resetPanels(['parallel-reference', 'parallel-main'])
                parallelSyncResetOnActivateRef.current = false
            }
            syncVideoStateFromPanel('parallel-main')
        })
        return () => window.cancelAnimationFrame(frameId)
    }, [
        activeExtraImage?.id,
        activeExtraImage?.url,
        mainImageKey,
        parallelSyncLock,
        resetPanels,
        shouldPreserveReferenceTransform,
        syncVideoStateFromPanel,
        viewMode,
    ])

    useEffect(() => {
        if (viewMode !== 'multiple' || !multipleSyncLocks?.some(Boolean)) return undefined
        const sourcePanelId = multipleSyncLocks[0] ? 'multiple-0' : 'multiple-1'
        const frameId = window.requestAnimationFrame(() => {
            // 槽位或素材变化后统一重置连通画布，并以同一源面板恢复视频同步状态。
            resetPanels(getConnectedPanelIds(sourcePanelId))
            syncVideoStateFromPanel(sourcePanelId)
        })
        return () => window.cancelAnimationFrame(frameId)
    }, [getConnectedPanelIds, multipleSyncLocks, multipleSyncSignature, resetPanels, syncVideoStateFromPanel, viewMode])

    useEffect(() => {
        prevMainImageKeyRef.current = mainImageKey
        prevReferenceLabelRef.current = currentReferenceLabel
    }, [currentReferenceLabel, mainImageKey])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.code !== 'Space' || event.repeat) return
            const target = event.target
            if (target?.closest?.('input, textarea, [contenteditable="true"]')) return

            const panelId = viewMode === 'multiple'
                ? `multiple-${multipleItems.find(item => item.imageIndex === context.currentIndex)?.slotIndex}`
                : viewMode === 'parallel' ? 'parallel-main' : 'single-main'
            if (!panelId || panelId.endsWith('undefined')) return
            event.preventDefault()
            handleVideoToggle(panelId)
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [context.currentIndex, handleVideoToggle, multipleItems, viewMode])

    return (
        <div className={styles.PicViewerCenter}>
            <div className={`${styles.CanvasArea} ${styles[viewMode]}`}>
                {viewMode === 'multiple' ? (
                    multipleItems.map((item, index) => {
                        const multipleCanvasItem = {
                            ...item.image,
                            name: item.image?.name || '交付图',
                            annotations: item.image?.annotations || [],
                            readonly: item.image?.readOnly,
                            downloadable: false,
                            pinned: item.pinned,
                            extraRender: null,
                        }
                        const multipleShowFloatingLabelTool = floatingLabelToolVisibleStatuses.includes(item.image?.status)
                        const confirmText = item.canAdvance && !item.pinned ? '保存，下一张' : '保存'
                        const isVideo = isVideoUrl(item.image?.url)

                        return (
                            <div className={styles.MultipleColumn} key={item.slotIndex}>
                                <CanvasPanel
                                    panelRef={(instance) => setPanelRef(`multiple-${item.slotIndex}`, instance)}
                                    onTransform={(nextTransform, meta) => handlePanelTransform(`multiple-${item.slotIndex}`, nextTransform, meta)}
                                    onVideoEvent={(event) => handleVideoEvent(`multiple-${item.slotIndex}`, event)}
                                    onVideoToggle={() => handleVideoToggle(`multiple-${item.slotIndex}`)}
                                    item={multipleCanvasItem}
                                    onLabel={(labels) => onMultipleAnnotationChange?.(item.imageIndex, labels)}
                                    showFloatingLabelTool={multipleShowFloatingLabelTool}
                                    onClick={() => onMultipleFocus?.(item.imageIndex)}
                                    onConfirm={() => onMultipleConfirm?.(item.imageIndex)}
                                    confirmText={confirmText}
                                    dropSlot={viewMode === 'multiple' ? {
                                        slotIndex: item.slotIndex,
                                        imageIndex: item.imageIndex,
                                        pinned: item.pinned,
                                        onDrop: onMultipleDrop,
                                    } : null}
                                    overlay={(
                                        <div
                                            className={`${styles.PinIcon} ${item.pinned ? styles.pinned : ''}`}
                                            style={isVideo ? {bottom: 52} : null}
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                onMultiplePinToggle?.(item.slotIndex)
                                            }}
                                            title="固定图片"
                                        >
                                            <Iconfont type="icon-fixed_fill" style={{ fontSize: 16 }} />
                                        </div>
                                    )}
                                    namePlacement="right"
                                /> 
                                {/* 同步锁定按钮 */}
                                {index !== 0 ? (
                                    <SyncLock
                                        leftMaterial={multipleItems[index - 1]?.image}
                                        rightMaterial={item.image}
                                        isActive={!!multipleSyncLocks?.[index - 1]}
                                        onClick={() => activateMultipleSyncLock(index - 1)}
                                    />
                                ) : null}
                            </div>
                        )
                    })
                ) : null}
                {viewMode === 'parallel' && referenceCanvasItem ? (
                    <>
                        <CanvasPanel
                            panelRef={(instance) => setPanelRef('parallel-reference', instance)}
                            onTransform={(nextTransform, meta) => handlePanelTransform('parallel-reference', nextTransform, meta)}
                            onVideoEvent={(event) => handleVideoEvent('parallel-reference', event)}
                            onVideoToggle={() => handleVideoToggle('parallel-reference')}
                            initialTransform={preservedReferenceTransform}
                            item={referenceCanvasItem}
                            onLabel={noop}
                            showFloatingLabelTool={referenceCanvasShowFloatingLabelTool}
                            allowReadonlyLabelTool
                            namePlacement="right"

                        />
                        <CanvasPanel
                            panelRef={(instance) => setPanelRef('parallel-main', instance)}
                            onTransform={(nextTransform, meta) => handlePanelTransform('parallel-main', nextTransform, meta)}
                            onVideoEvent={(event) => handleVideoEvent('parallel-main', event)}
                            onVideoToggle={() => handleVideoToggle('parallel-main')}
                            initialTransform={preservedMainTransform}
                            item={mainCanvasItem}
                            onLabel={onAnnotationChange}
                            onConfirm={onConfirm}
                            showFloatingLabelTool={showFloatingLabelTool}
                            namePlacement="right"
                        />
                        {/* 同步锁定按钮 */}
                        <SyncLock
                            leftMaterial={referenceCanvasItem}
                            rightMaterial={mainCanvasItem}
                            style={{left: "50%"}}
                            isActive={parallelSyncLock}
                            onClick={activateParallelSyncLock}
                        />
                    </>
                ) : null}
                {viewMode === 'compare' && referenceCanvasItem ? (
                    <ComparePanel
                        leftItem={referenceCanvasItem}
                        rightItem={mainCanvasItem}
                        comparePosition={comparePosition}
                        onComparePositionChange={setComparePosition}
                    />
                ) : null}
                {viewMode === 'single' || (viewMode !== 'multiple' && !referenceCanvasItem) ? (
                    <CanvasPanel
                        panelRef={(instance) => setPanelRef('single-main', instance)}
                        onVideoEvent={(event) => handleVideoEvent('single-main', event)}
                        onVideoToggle={() => handleVideoToggle('single-main')}
                        item={mainCanvasItem}
                        onLabel={onAnnotationChange}
                        onConfirm={onConfirm}
                        showFloatingLabelTool={showFloatingLabelTool}
                    />
                ) : null}
            </div>
            <div className={styles.BottomBar}>
            {
                viewMode === "multiple" ?
                <PicViewerBottomMultiple
                    items={multipleItems}
                    onFocus={onMultipleFocus}
                    onStatusChange={onMultipleStatusChange}
                /> :
                <PicViewerBottom
                    image={image}
                    onDownload={onDownload}
                    extraButtons={extraButtons}
                    hasPrev={hasPrev}
                    hasNext={hasNext}
                    onPrev={onPrev}
                    onNext={onNext}
                    onStatusChange={onStatusChange}
                />
            }
            </div>
            {shouldShowModeChanger ? (
                <div className={styles.ModeChanger}>
                    <ModeSwitcher options={availableModes} value={viewMode} onChange={setViewMode} />
                </div>
            ) : null }
            {viewMode === "multiple" ?
            <>
            <div className={`${styles.PreNextBtn} ${styles.PrevBtn}`} onClick={e => {
                e.stopPropagation()
                onMultipleBatchShift(-1)
            }}>
                <LeftOutlined />
            </div>
            <div className={`${styles.PreNextBtn} ${styles.NextBtn}`} onClick={e => {
                e.stopPropagation()
                onMultipleBatchShift(1)
            }}>
                <RightOutlined />
            </div>
            </> : null}
        </div>
    )
}

function SyncLock ({onClick, isActive = false, style = {}, leftMaterial, rightMaterial}) {
    // 任一侧为视频时上移同步锁，避免与底部视频控制条重叠。
    const hasVideo = isVideoUrl(leftMaterial?.url) || isVideoUrl(rightMaterial?.url)
    // 教程
    const [tutorialOpen, setTutorialOpen] = useState(false)
    useEffect(() => {
        const syncLockTutorialDisabled = localStorage.getItem('syncLock_tutorial_disabled')
        if (syncLockTutorialDisabled) {
            setTutorialOpen(false)
        } else {
            setTutorialOpen(true)
        }
    }, [])
    const onOpenChange = useCallback(open => {
        if (!open) {
            setTutorialOpen(false)
        } else {
            const syncLockTutorialDisabled = localStorage.getItem('syncLock_tutorial_disabled')
            if (syncLockTutorialDisabled) {
                setTutorialOpen(false)
            } else {
                setTutorialOpen(true)
            }
        }
    }, [])
    const tutorial = (
        <div>
            <div style={{color: "#1D1B1B", fontWeight: 700, marginBottom: 6}}>支持同步查看啦~~</div>
            <div style={{color: "#737A84", fontWeight: 400, marginBottom: 12}}>点击开启后，可同步缩放、移动以及视频播放进度</div>
            <div style={{textAlign: "end"}}>
                <Button size="small" type="primary" onClick={() => {
                    setTutorialOpen(false)
                    localStorage.setItem('syncLock_tutorial_disabled', 1)
                }}>知道了</Button>
            </div>
        </div>
    )
     
    return (
        <Popover
            open={tutorialOpen}
            content={tutorial}
            placement="top"
            arrow={false}
            onOpenChange={onOpenChange}
        >
            <div
                title="同步查看"
                className={`${styles.SyncLockIcon} ${isActive ? styles.active : ''}`}
                style={hasVideo ? {...style, bottom: 52} : style}
                onClick={onClick}
            >
                <Iconfont type="icon-link" style={{ fontSize: 16 }} />
            </div>
        </Popover>
    )
}

function ModeSwitcher ({ options, value, onChange }) {
    return (<div className={styles.ModeSwitcher}>
        <ConfigProvider
            theme={{
                components: {
                    Segmented: {
                        itemSelectedColor: '#006EFF',
                        trackPadding: 4,
                    },
                },
            }}
        >
            <Segmented
                size="large"
                options={options}
                value={value}
                onChange={onChange}
            />
        </ConfigProvider>
    </div>)
}

function CanvasPanel({
    panelRef,
    item,
    onLabel,
    onTransform,
    onVideoEvent,
    onVideoToggle,
    initialTransform,
    showFloatingLabelTool = false,
    allowReadonlyLabelTool = false,
    namePlacement,
    isActive = false,
    onClick,
    overlay,
    onConfirm,
    confirmText,
    dropSlot,
}) {
    const context = useContext(PictureViewerContext)
    const {
        floatingLabelToolPlacement = 'center',
        canvasToolsDisabled = false,
    } = context.controls

    const renderKey = item?.id || item?.imageId || item?.url
    const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
        accept: MULTIPLE_IMAGE_DRAG_TYPE,
        canDrop: (dragItem) => {
            if (!dropSlot?.onDrop) return false
            if (dropSlot.pinned) return false
            return dragItem?.imageIndex !== dropSlot.imageIndex
        },
        drop: (dragItem) => {
            dropSlot?.onDrop?.(dragItem?.imageIndex, dropSlot.slotIndex)
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    }), [dropSlot])

    if (!item?.url) return null

    return (
        <div
            ref={dropSlot?.onDrop ? dropRef : null}
            className={`${styles.CanvasPanel} ${isActive ? styles.active : ''} ${canDrop ? styles.dropReady : ''} ${isOver && canDrop ? styles.dropOver : ''}`}
            onClick={onClick}
        >
            <ImageRenderBox
                ref={panelRef}
                key={renderKey}
                item={item}
                onLabel={(labels) => onLabel?.(labels)}
                onTransform={onTransform}
                onVideoEvent={onVideoEvent}
                onVideoToggle={onVideoToggle}
                initialTransform={initialTransform}
                downloadable={false}
                allowReadonlyLabelTool={allowReadonlyLabelTool}
                labelToolDisplay="floating"
                labelToolPlacement={floatingLabelToolPlacement}
                labelToolVisible={showFloatingLabelTool}
                namePlacement={namePlacement}
                canvasToolsDisabled={canvasToolsDisabled}
                onConfirm={onConfirm}
                confirmText={confirmText}
            />
            {canDrop ? <div className={`${styles.DropMask} ${isOver ? styles.visible : ''}`}>拖拽到此替换</div> : null}
            {overlay}
        </div>
    )
}

function ComparePanel({
    leftItem,
    rightItem,
    comparePosition,
    onComparePositionChange,
}) {
    const stageRef = useRef(null)
    const dragStateRef = useRef(null)
    const [compareZoom, setCompareZoom] = useState(1)
    const [compareZoomOrigin, setCompareZoomOrigin] = useState({ x: 50, y: 50 })
    const [compareOffset, setCompareOffset] = useState({ x: 0, y: 0 })
    const [dragType, setDragType] = useState(null)
    const currentLeftLabel = useMemo(() => leftItem?.label || leftItem?.name || null, [leftItem?.label, leftItem?.name])
    const currentRightItemKey = getCanvasItemKey(rightItem)
    const prevLeftLabelRef = useRef(leftItem?.label || leftItem?.name || null)
    const prevRightItemKeyRef = useRef(getCanvasItemKey(rightItem))

    useEffect(() => {
        const prevLeftLabel = prevLeftLabelRef.current
        const prevRightItemKey = prevRightItemKeyRef.current
        const shouldPreserveViewState = !!prevRightItemKey
            && prevRightItemKey !== currentRightItemKey
            && !!prevLeftLabel
            && prevLeftLabel === currentLeftLabel

        if (!shouldPreserveViewState) {
            setCompareZoom(1)
            setCompareZoomOrigin({ x: 50, y: 50 })
            setCompareOffset({ x: 0, y: 0 })
        }

        prevLeftLabelRef.current = currentLeftLabel
        prevRightItemKeyRef.current = currentRightItemKey
    }, [currentLeftLabel, currentRightItemKey])

    const handleWheel = useCallback((event) => {
        event.preventDefault()
        const rect = event.currentTarget.getBoundingClientRect()
        const nextOrigin = {
            x: ((event.clientX - rect.left) / rect.width) * 100,
            y: ((event.clientY - rect.top) / rect.height) * 100,
        }
        const delta = event.deltaY < 0 ? 0.2 : -0.2
        const nextZoom = Math.min(4, Math.max(0.5, Number((compareZoom + delta).toFixed(2))))
        setCompareZoomOrigin(nextOrigin)
        setCompareZoom(nextZoom)
    }, [compareZoom])

    const clampComparePosition = useCallback((nextPosition) => {
        return Math.min(100, Math.max(0, nextPosition))
    }, [])

    useEffect(() => {
        const handlePointerMove = (event) => {
            const dragState = dragStateRef.current
            if (!dragState || event.pointerId !== dragState.pointerId) return
            event.preventDefault()
            if (dragState.type === 'divider') {
                const stageElement = stageRef.current
                if (!stageElement) return
                const rect = stageElement.getBoundingClientRect()
                const nextPosition = ((event.clientX - rect.left) / rect.width) * 100
                onComparePositionChange(clampComparePosition(nextPosition))
                return
            }
            const deltaX = event.clientX - dragState.startX
            const deltaY = event.clientY - dragState.startY
            setCompareOffset({
                x: dragState.startOffset.x + deltaX,
                y: dragState.startOffset.y + deltaY,
            })
        }

        const handlePointerEnd = (event) => {
            const dragState = dragStateRef.current
            if (!dragState || event.pointerId !== dragState.pointerId) return
            dragStateRef.current = null
            setDragType(null)
        }

        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', handlePointerEnd)
        window.addEventListener('pointercancel', handlePointerEnd)

        return () => {
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', handlePointerEnd)
            window.removeEventListener('pointercancel', handlePointerEnd)
        }
    }, [clampComparePosition, onComparePositionChange])

    const startDrag = useCallback((type, event) => {
        if (event.button !== 0) return
        event.preventDefault()
        dragStateRef.current = {
            type,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startOffset: compareOffset,
        }
        setDragType(type)
    }, [compareOffset])

    const handleStagePointerDown = useCallback((event) => {
        startDrag('image', event)
    }, [startDrag])

    const handleDividerPointerDown = useCallback((event) => {
        event.stopPropagation()
        startDrag('divider', event)
    }, [startDrag])

    const panStyle = useMemo(() => ({
        transform: `translate3d(${compareOffset.x}px, ${compareOffset.y}px, 0)`,
    }), [compareOffset.x, compareOffset.y])

    const zoomStyle = useMemo(() => ({
        transform: `scale(${compareZoom})`,
        transformOrigin: `${compareZoomOrigin.x}% ${compareZoomOrigin.y}%`,
    }), [compareZoom, compareZoomOrigin.x, compareZoomOrigin.y])

    return (
        <div className={styles.ComparePanel}>
            <div
                ref={stageRef}
                className={`${styles.CompareStage} ${dragType === 'image' ? styles.CompareStageDragging : ''} ${dragType === 'divider' ? styles.CompareStageResizing : ''}`}
                onWheel={handleWheel}
                onPointerDown={handleStagePointerDown}
            >
                <div
                    className={`${styles.CompareLayer} ${styles.CompareLeft}`}
                    style={{ clipPath: `inset(0 ${100 - comparePosition}% 0 0)` }}
                >
                    <div className={styles.ComparePanContent} style={panStyle}>
                        <div className={styles.CompareZoomContent} style={zoomStyle}>
                            <SimpleMedia item={leftItem} />
                        </div>
                    </div>
                    <div className={styles.CompareTag} style={{right:0}}>{leftItem?.name || leftItem?.label || '参考图'}</div>
                </div>
                <div
                    className={`${styles.CompareLayer} ${styles.CompareRight}`}
                    style={{ clipPath: `inset(0 0 0 ${comparePosition}%)` }}
                >
                    <div className={styles.ComparePanContent} style={panStyle}>
                        <div className={styles.CompareZoomContent} style={zoomStyle}>
                            <SimpleMedia item={rightItem} />
                        </div>
                    </div>
                    <div className={styles.CompareTag} style={{right:0}}>{rightItem?.name || '交付图'}</div>
                </div>
                <div
                    className={styles.CompareDivider}
                    style={{ left: `${comparePosition}%` }}
                    onPointerDown={handleDividerPointerDown}
                >
                    <div className={styles.CompareHandle}>
                        <span />
                    </div>
                </div>
            </div>
        </div>
    )
}

function SimpleMedia({ item }) {
    if (!item?.url) return null
    return (
        <div className={styles.SimpleMedia}>
            {isVideoUrl(item.url) ? (
                <video
                    className={styles.SimpleVideo}
                    src={item.url}
                    controls
                    autoPlay
                    loop
                    preload="metadata"
                    crossOrigin="anonymous"
                    draggable={false}
                />
            ) : (
                <div className={styles.SimpleImageWrap}>
                    <img src={item.url} alt={item.name || item.label || ''} className={styles.SimpleImage} draggable={false} />
                    {item?.overlayUrl ? (
                        <img
                            src={item.overlayUrl}
                            alt=""
                            aria-hidden="true"
                            className={`${styles.SimpleImage} ${styles.SimpleOverlay}`}
                            draggable={false}
                        />
                    ) : null}
                </div>
            )}
        </div>
    )
}

function useBlueMaskOverlay(maskUrl) {
    const [overlay, setOverlay] = useState({ source: null, url: null })

    useEffect(() => {
        let objectUrl = null
        let disposed = false
        setOverlay({ source: null, url: null })
        if (!maskUrl) return undefined

        const worker = new Worker(`${process.env.PUBLIC_URL}/webworker/binary-mask-overlay-worker.js`)
        const color = (sessionStorage.getItem('foreground_color_data') || '30,144,255')
            .split(',')
            .map(Number)

        worker.onmessage = event => {
            worker.terminate()
            if (!event.data.success) {
                console.warn('Mask overlay worker failed:', event.data.error)
                return
            }
            objectUrl = URL.createObjectURL(event.data.blob)
            if (disposed) {
                URL.revokeObjectURL(objectUrl)
                return
            }
            setOverlay({ source: maskUrl, url: objectUrl })
        }
        worker.onerror = event => {
            worker.terminate()
            console.warn('Mask overlay worker failed:', event.message)
        }
        worker.postMessage({ url: maskUrl, color, alpha: 204 })

        return () => {
            disposed = true
            worker.terminate()
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [maskUrl])

    return overlay.source === maskUrl ? overlay.url : null
}

// 常见视频扩展名，用于判断 url 是否为视频资源
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i

/** 根据 url 扩展名判断是否为视频 */
function isVideoUrl(url) {
    if (!url) return false
    return VIDEO_EXTENSIONS.test(url)
}

function getCanvasItemKey(item) {
    return item?.id || item?.imageId || item?.url || null
}
